import { Base58Util } from "@solomon-labs/base58";
import { create } from "zustand";

export interface RememberedSquad {
    address: string;
    vaultAddress: string;
}

const STORAGE_KEY = "solomon:retail-squads:v1";
const SELECTION_STORAGE_KEY = "solomon:retail-account-selection:v1";
const MAX_SQUADS = 10;
type AccountSelections = Record<string, RememberedSquad | null>;

export function parseRememberedSquads(raw: string | null): Record<string, RememberedSquad[]> {
    try {
        const value: unknown = JSON.parse(raw ?? "{}");
        if (!value || typeof value !== "object" || Array.isArray(value)) return {};
        const result: Record<string, RememberedSquad[]> = {};
        for (const [signer, entries] of Object.entries(value)) {
            if (!Base58Util.isValidPublicKey(signer) || !Array.isArray(entries)) continue;
            const valid = entries.filter((entry: unknown): entry is RememberedSquad => {
                if (!entry || typeof entry !== "object") return false;
                const row = entry as Record<string, unknown>;
                return (
                    typeof row.address === "string" &&
                    Base58Util.isValidPublicKey(row.address) &&
                    typeof row.vaultAddress === "string" &&
                    Base58Util.isValidPublicKey(row.vaultAddress)
                );
            });
            result[signer] = Array.from(
                new Map(
                    valid.map((entry) => [
                        entry.address,
                        {
                            address: entry.address,
                            vaultAddress: entry.vaultAddress
                        }
                    ])
                ).values()
            ).slice(0, MAX_SQUADS);
        }
        return result;
    } catch {
        return {};
    }
}

function readRemembered(): Record<string, RememberedSquad[]> {
    try {
        return parseRememberedSquads(window.localStorage.getItem(STORAGE_KEY));
    } catch {
        return {};
    }
}

function saveRemembered(remembered: Record<string, RememberedSquad[]>): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(remembered));
    } catch {
        /* Browsing still works without storage. */
    }
}

function readSelections(): AccountSelections {
    try {
        const raw: unknown = JSON.parse(window.localStorage.getItem(SELECTION_STORAGE_KEY) ?? "{}");
        if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
        const suggestions = parseRememberedSquads(
            JSON.stringify(
                Object.fromEntries(Object.entries(raw).map(([signer, squad]) => [signer, squad ? [squad] : []]))
            )
        );
        return Object.fromEntries(Object.entries(suggestions).map(([signer, squads]) => [signer, squads[0] ?? null]));
    } catch {
        return {};
    }
}

function saveSelections(selected: AccountSelections): void {
    try {
        window.localStorage.setItem(SELECTION_STORAGE_KEY, JSON.stringify(selected));
    } catch {
        /* Selection still works without storage. */
    }
}

interface RetailAccountState {
    retryNonce: number;
    retry: () => void;
    remembered: Record<string, RememberedSquad[]>;
    selected: AccountSelections;
    select: (signer: string, squad: RememberedSquad | null) => void;
    remember: (signer: string, squad: RememberedSquad) => void;
    forget: (signer: string, address: string) => void;
}

/** Stored addresses are suggestions only; useRetailAccount revalidates them before use. */
export const useRetailAccountStore = create<RetailAccountState>((set, get) => ({
    retryNonce: 0,
    retry: (): void => set((state) => ({ retryNonce: state.retryNonce + 1 })),
    remembered: readRemembered(),
    selected: readSelections(),
    select: (signer, squad): void =>
        set((state) => {
            const selected = { ...state.selected, [signer]: squad };
            // Also retain discovery-only and older restored selections when
            // switching to Personal, so returning never depends on discovery.
            const suggestion = squad ?? state.selected[signer];
            const remembered = suggestion
                ? {
                      ...state.remembered,
                      [signer]: [
                          suggestion,
                          ...(state.remembered[signer] ?? []).filter((entry) => entry.address !== suggestion.address)
                      ].slice(0, MAX_SQUADS)
                  }
                : state.remembered;
            saveRemembered(remembered);
            saveSelections(selected);
            return { remembered, selected };
        }),
    remember: (signer, squad): void => get().select(signer, squad),
    forget: (signer, address): void =>
        set((state) => {
            const remembered = {
                ...state.remembered,
                [signer]: (state.remembered[signer] ?? []).filter((entry) => entry.address !== address)
            };
            saveRemembered(remembered);
            const selected =
                state.selected[signer]?.address === address ? { ...state.selected, [signer]: null } : state.selected;
            saveSelections(selected);
            return { remembered, selected };
        })
}));

/**
 * The vault of the Squads multisig selected for `signer`, or null when
 * multisig mode is off (nothing selected). This is the selection as stored,
 * the same vault the account menu's combobox shows; `useRetailAccount`
 * still revalidates membership before anything acts on it.
 */
export function useSelectedSquadsVault(signer: string): string | null {
    return useRetailAccountStore((state) => (signer ? (state.selected[signer]?.vaultAddress ?? null) : null));
}
